import React, { useEffect, useMemo, useState } from "react";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Dropzone from "../../components/ui/Dropzone";
import Field from "../../components/ui/Field";
import Input from "../../components/ui/Input";
import PageHeader from "../../components/ui/PageHeader";
import Switch from "../../components/ui/Switch";
import Textarea from "../../components/ui/Textarea";
import Select from "../../components/ui/Select";
import {
    createSlideshowRequest,
    getSlideshowsRequest,
    updateSlideshowRequest,
    uploadSlideshowImageRequest,
} from "../../services/api/content.api";
import { getToursRequest } from "../../services/api/event-content.api";
import { SlideshowItem } from "../../services/types/content.types";
import { TourItem, ToursResponseShape } from "../../services/types/event-content.types";

interface FormState {
    titleRu: string;
    titleEn: string;
    titleDe: string;
    titleUa: string;
    shortDescRu: string;
    shortDescEn: string;
    shortDescDe: string;
    shortDescUa: string;
    link: string;
    buttonTextRu: string;
    buttonTextEn: string;
    buttonTextDe: string;
    buttonTextUa: string;
    buttonLinkRu: string;
    buttonLinkEn: string;
    buttonLinkDe: string;
    buttonLinkUa: string;
    sort: string;
    startAt: string;
    endAt: string;
    imageId: string;
    tourId: string;
    isActive: boolean;
    showOnMainSite: boolean;
    showOnUaSite: boolean;
}

const initialFormState: FormState = {
    titleRu: "",
    titleEn: "",
    titleDe: "",
    titleUa: "",
    shortDescRu: "",
    shortDescEn: "",
    shortDescDe: "",
    shortDescUa: "",
    link: "",
    buttonTextRu: "",
    buttonTextEn: "",
    buttonTextDe: "",
    buttonTextUa: "",
    buttonLinkRu: "",
    buttonLinkEn: "",
    buttonLinkDe: "",
    buttonLinkUa: "",
    sort: "",
    startAt: "",
    endAt: "",
    imageId: "",
    tourId: "",
    isActive: true,
    showOnMainSite: true,
    showOnUaSite: false,
};

const normalizeToursResponse = (
    response: ToursResponseShape | TourItem[],
): TourItem[] => {
    if (Array.isArray(response)) {
        return response;
    }

    if (response.items && Array.isArray(response.items)) {
        return response.items;
    }

    if (response.data && Array.isArray(response.data)) {
        return response.data;
    }

    return [];
};

const SlideshowsPage: React.FC = () => {
    const [items, setItems] = useState<SlideshowItem[]>([]);
    const [tours, setTours] = useState<TourItem[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [formState, setFormState] = useState<FormState>(initialFormState);
    const [searchValue, setSearchValue] = useState("");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [pageError, setPageError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const selectedItem = useMemo(() => {
        return items.find((item) => item.id === selectedId) ?? null;
    }, [items, selectedId]);

    const eventOptions = useMemo(() => {
        return tours.map((item) => ({
            value: String(item.id),
            label: item.name_ru ?? item.name_en ?? item.name_de ?? `ID: ${item.id}`,
        }));
    }, [tours]);

    useEffect(() => {
        if (!selectedFile) {
            setLocalPreviewUrl(null);
            return;
        }

        const objectUrl = URL.createObjectURL(selectedFile);
        setLocalPreviewUrl(objectUrl);

        return () => URL.revokeObjectURL(objectUrl);
    }, [selectedFile]);

    const loadData = async () => {
        setIsLoading(true);
        setPageError(null);

        try {
            const [slideshowsResponse, toursResponse] = await Promise.all([
                getSlideshowsRequest(),
                getToursRequest({
                    page: 1,
                    size: 200,
                }),
            ]);

            setItems(slideshowsResponse);
            setTours(normalizeToursResponse(toursResponse));
        } catch (error) {
            setPageError(
                error instanceof Error ? error.message : "Не удалось загрузить слайды и туры",
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void loadData();
    }, []);

    const filteredItems = useMemo(() => {
        const query = searchValue.trim().toLowerCase();

        if (!query) {
            return items;
        }

        return items.filter((item) => {
            return (
                (item.title_ru ?? "").toLowerCase().includes(query) ||
                (item.short_desc_ru ?? "").toLowerCase().includes(query) ||
                (item.link ?? "").toLowerCase().includes(query)
            );
        });
    }, [items, searchValue]);

    const resetForm = () => {
        setSelectedId(null);
        setFormState(initialFormState);
        setSelectedFile(null);
        setLocalPreviewUrl(null);
        setFormError(null);
        setSuccessMessage(null);
    };

    const normalizeDateTimeLocal = (value?: string | null): string => {
        if (!value) {
            return "";
        }

        const parsed = new Date(value);

        if (Number.isNaN(parsed.getTime())) {
            return "";
        }

        const year = parsed.getFullYear();
        const month = String(parsed.getMonth() + 1).padStart(2, "0");
        const day = String(parsed.getDate()).padStart(2, "0");
        const hours = String(parsed.getHours()).padStart(2, "0");
        const minutes = String(parsed.getMinutes()).padStart(2, "0");

        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const handleSelect = (item: SlideshowItem) => {
        setSelectedId(item.id);
        setFormState({
            titleRu: item.title_ru ?? "",
            titleEn: item.title_en ?? "",
            titleDe: item.title_de ?? "",
            titleUa: item.title_ua ?? "",
            shortDescRu: item.short_desc_ru ?? "",
            shortDescEn: item.short_desc_en ?? "",
            shortDescDe: item.short_desc_de ?? "",
            shortDescUa: item.short_desc_ua ?? "",
            link: item.link ?? "",
            buttonTextRu: item.button_text_ru ?? "",
            buttonTextEn: item.button_text_en ?? "",
            buttonTextDe: item.button_text_de ?? "",
            buttonTextUa: item.button_text_ua ?? "",
            buttonLinkRu: item.button_link_ru ?? "",
            buttonLinkEn: item.button_link_en ?? "",
            buttonLinkDe: item.button_link_de ?? "",
            buttonLinkUa: item.button_link_ua ?? "",
            sort: item.sort !== null && item.sort !== undefined ? String(item.sort) : "",
            startAt: normalizeDateTimeLocal(item.start_at),
            endAt: normalizeDateTimeLocal(item.end_at),
            imageId: item.image_id ?? "",
            tourId: item.tour_id ?? "",
            isActive: item.is_active,
            showOnMainSite: item.show_on_main_site ?? true,
            showOnUaSite: item.show_on_ua_site ?? false,
        });
        setSelectedFile(null);
        setLocalPreviewUrl(null);
        setFormError(null);
        setSuccessMessage(null);
    };

    const uploadSelectedImage = async (file: File, slideshowId: string) => {
        setIsUploadingImage(true);
        setFormError(null);
        setSuccessMessage(null);

        try {
            const response = await uploadSlideshowImageRequest(slideshowId, file);
            const nextImageId = response.image_id ?? response.id ?? "";
            const nextImageUrl = response.image_url ?? response.url ?? null;

            if (!nextImageId) {
                throw new Error("API не вернул image_id после загрузки изображения.");
            }

            setFormState((prev) => ({
                ...prev,
                imageId: nextImageId,
            }));

            setItems((prev) =>
                prev.map((item) =>
                    item.id === slideshowId
                        ? {
                            ...item,
                            image_id: nextImageId,
                            image_url: nextImageUrl ?? item.image_url ?? null,
                        }
                        : item,
                ),
            );

            setSuccessMessage("Изображение успешно загружено и привязано к слайду.");
        } catch (error) {
            setFormError(
                error instanceof Error ? error.message : "Не удалось загрузить изображение",
            );
        } finally {
            setIsUploadingImage(false);
        }
    };

    const handleFileChange = async (file: File | null) => {
        setSelectedFile(file);

        if (!file) {
            setFormState((prev) => ({
                ...prev,
                imageId: "",
            }));
            return;
        }

        if (!selectedId) {
            setFormError("Сначала сохраните слайд, затем загрузите изображение.");
            return;
        }

        await uploadSelectedImage(file, selectedId);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFormError(null);
        setSuccessMessage(null);

        setIsSaving(true);

        try {
            const payload = {
                title_ru: formState.titleRu.trim() || null,
                title_en: formState.titleEn.trim() || null,
                title_de: formState.titleDe.trim() || null,
                title_ua: formState.titleUa.trim() || null,
                short_desc_ru: formState.shortDescRu.trim() || null,
                short_desc_en: formState.shortDescEn.trim() || null,
                short_desc_de: formState.shortDescDe.trim() || null,
                short_desc_ua: formState.shortDescUa.trim() || null,
                link: formState.link.trim() || null,
                // Отправляем пустую строку, а не null: на бэкенде PATCH игнорирует
                // null-значения, поэтому иначе очистить поле было бы невозможно.
                button_text_ru: formState.buttonTextRu.trim(),
                button_text_en: formState.buttonTextEn.trim(),
                button_text_de: formState.buttonTextDe.trim(),
                button_text_ua: formState.buttonTextUa.trim() || null,
                button_link_ru: formState.buttonLinkRu.trim(),
                button_link_en: formState.buttonLinkEn.trim(),
                button_link_de: formState.buttonLinkDe.trim(),
                button_link_ua: formState.buttonLinkUa.trim() || null,
                image_id: formState.imageId || null,
                tour_id: formState.tourId || null,
                sort: formState.sort.trim() ? Number(formState.sort) : null,
                start_at: formState.startAt ? new Date(formState.startAt).toISOString() : null,
                end_at: formState.endAt ? new Date(formState.endAt).toISOString() : null,
                is_active: formState.isActive,
                show_on_main_site: formState.showOnMainSite,
                show_on_ua_site: formState.showOnUaSite,
            };

            const savedItem = selectedId
                ? await updateSlideshowRequest(selectedId, payload)
                : await createSlideshowRequest(payload);

            setItems((prev) => {
                const exists = prev.some((item) => item.id === savedItem.id);

                if (exists) {
                    return prev.map((item) => (item.id === savedItem.id ? savedItem : item));
                }

                return [savedItem, ...prev];
            });

            handleSelect(savedItem);
            setSuccessMessage(selectedId ? "Слайд обновлён." : "Слайд создан.");
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Ошибка сохранения слайда");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="page-content">
            <PageHeader
                title="Слайдшоу"
                description="Промо-слайды сайта с изображением, коротким описанием, ссылкой, периодом показа и статусом."
                actions={<Button onClick={resetForm}>Новый слайд</Button>}
            />

            <section className="content-split artist-layout">
                <Card className="content-list-card">
                    <div className="section-toolbar">
                        <div>
                            <h3 className="card-title">Список слайдов</h3>
                            <p className="card-text">
                                Все элементы остаются в списке, активность показывается через статус.
                            </p>
                        </div>

                        <div className="section-toolbar-controls">
                            <Input
                                type="text"
                                placeholder="Поиск по заголовку, описанию или ссылке..."
                                value={searchValue}
                                onChange={(event) => setSearchValue(event.target.value)}
                            />
                        </div>
                    </div>

                    {pageError ? (
                        <div className="form-global-error">{pageError}</div>
                    ) : isLoading ? (
                        <div className="table-skeleton">
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="empty-block">
                            <h3 className="empty-block-title">Нет слайдов</h3>
                            <p className="empty-block-text">Создайте первый слайд для витрины сайта.</p>
                        </div>
                    ) : (
                        <div className="slideshow-grid">
                            {filteredItems.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={`slideshow-card ${item.id === selectedId ? "slideshow-card-active" : ""}`}
                                    onClick={() => handleSelect(item)}
                                >
                                    <div className="slideshow-card-image-wrap">
                                        {localPreviewUrl && selectedId === item.id ? (
                                            <img
                                                src={localPreviewUrl}
                                                alt={item.title_ru ?? "Слайд"}
                                                className="slideshow-card-image"
                                            />
                                        ) : item.image_url ? (
                                            <img
                                                src={item.image_url}
                                                alt={item.title_ru ?? "Слайд"}
                                                className="slideshow-card-image"
                                            />
                                        ) : (
                                            <div className="slideshow-card-image-placeholder">Без фото</div>
                                        )}
                                    </div>

                                    <div className="slideshow-card-content">
                                        <span className="slideshow-card-title">
                                            {item.title_ru || "Без заголовка"}
                                        </span>
                                        <span className="slideshow-card-subtitle">
                                            {item.short_desc_ru || item.link || "Без описания"}
                                        </span>
                                        {/* На каких сайтах показывается слайд */}
                                        <span className="tour-sites-badges">
                                            {item.show_on_main_site !== false ? (
                                                <span className="tour-site-badge" title="Показывается на основном сайте">Основной</span>
                                            ) : null}
                                            {item.show_on_ua_site ? (
                                                <span className="tour-site-badge tour-site-badge-ua" title="Показывается на втором сайте">UA</span>
                                            ) : null}
                                            {item.show_on_main_site === false && !item.show_on_ua_site ? (
                                                <span className="tour-site-badge tour-site-badge-none">Скрыт на сайтах</span>
                                            ) : null}
                                        </span>
                                        <span className="slideshow-card-period">
                                            {item.start_at || item.end_at
                                                ? `${item.start_at ?? "—"} → ${item.end_at ?? "—"}`
                                                : "Период не задан"}
                                        </span>
                                    </div>

                                    <span
                                        className={`status-dot ${
                                            item.is_active ? "status-dot-active" : "status-dot-inactive"
                                        }`}
                                        style={{ marginTop: "30px" }}
                                    />
                                </button>
                            ))}
                        </div>
                    )}
                </Card>

                <Card className="content-form-card">
                    <div className="editor-head">
                        <div>
                            <h3 className="card-title">
                                {selectedId ? "Редактирование слайда" : "Новый слайд"}
                            </h3>
                            <p className="card-text">
                                После выбора файла изображение сразу отправляется на backend и возвращает image_id.
                            </p>
                        </div>
                    </div>

                    <form className="editor-form" onSubmit={handleSubmit}>
                        <div className="form-grid form-grid-two-columns">
                            <Field label="Заголовок (RU)" htmlFor="slideshow-title-ru">
                                <Input
                                    id="slideshow-title-ru"
                                    value={formState.titleRu}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, titleRu: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Заголовок (EN)" htmlFor="slideshow-title-en">
                                <Input
                                    id="slideshow-title-en"
                                    value={formState.titleEn}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, titleEn: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Заголовок (DE)" htmlFor="slideshow-title-de">
                                <Input
                                    id="slideshow-title-de"
                                    value={formState.titleDe}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, titleDe: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Заголовок (UA)" htmlFor="slideshow-title-ua">
                                <Input
                                    id="slideshow-title-ua"
                                    value={formState.titleUa}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, titleUa: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Тур" htmlFor="slideshow-event-id">
                                <Select
                                    id="slideshow-event-id"
                                    options={eventOptions}
                                    value={formState.tourId}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, tourId: event.target.value }))
                                    }
                                    placeholder="Выберите тур"
                                />
                            </Field>

                            <Field label="Sort" htmlFor="slideshow-sort">
                                <Input
                                    id="slideshow-sort"
                                    value={formState.sort}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, sort: event.target.value }))
                                    }
                                />
                            </Field>
                        </div>

                        <Field label="Короткое описание (RU)" htmlFor="slideshow-short-desc-ru">
                            <Textarea
                                id="slideshow-short-desc-ru"
                                rows={3}
                                value={formState.shortDescRu}
                                onChange={(event) =>
                                    setFormState((prev) => ({ ...prev, shortDescRu: event.target.value }))
                                }
                            />
                        </Field>

                        <Field label="Короткое описание (EN)" htmlFor="slideshow-short-desc-en">
                            <Textarea
                                id="slideshow-short-desc-en"
                                rows={3}
                                value={formState.shortDescEn}
                                onChange={(event) =>
                                    setFormState((prev) => ({ ...prev, shortDescEn: event.target.value }))
                                }
                            />
                        </Field>

                        <Field label="Короткое описание (DE)" htmlFor="slideshow-short-desc-de">
                            <Textarea
                                id="slideshow-short-desc-de"
                                rows={3}
                                value={formState.shortDescDe}
                                onChange={(event) =>
                                    setFormState((prev) => ({ ...prev, shortDescDe: event.target.value }))
                                }
                            />
                        </Field>

                        <Field label="Короткое описание (UA)" htmlFor="slideshow-short-desc-ua">
                            <Textarea
                                id="slideshow-short-desc-ua"
                                rows={3}
                                value={formState.shortDescUa}
                                onChange={(event) =>
                                    setFormState((prev) => ({ ...prev, shortDescUa: event.target.value }))
                                }
                            />
                        </Field>

                        <div
                            style={{
                                marginTop: 8,
                                paddingTop: 12,
                                borderTop: "1px solid var(--border)",
                            }}
                        >
                            <h4 style={{margin: "0 0 4px"}}>Кнопка слайда</h4>
                            <p className="card-text" style={{margin: 0, fontSize: 12, color: "var(--text-muted)"}}>
                                Текст кнопки необязателен: если оставить пусто и выбран тур, на сайте
                                останется стандартная надпись «Купить билеты». Ссылки нужны для баннеров
                                без тура — тогда кнопка ведёт по адресу той языковой версии, которую
                                открыл посетитель. Если тур выбран, кнопка всегда ведёт на страницу тура,
                                а ссылки ниже не используются.
                            </p>
                        </div>

                        <div className="form-grid form-grid-two-columns">
                            <Field label="Текст кнопки (RU)" htmlFor="slideshow-button-text-ru">
                                <Input
                                    id="slideshow-button-text-ru"
                                    value={formState.buttonTextRu}
                                    placeholder="Купить билеты"
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, buttonTextRu: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Ссылка кнопки (RU)" htmlFor="slideshow-button-link-ru">
                                <Input
                                    id="slideshow-button-link-ru"
                                    value={formState.buttonLinkRu}
                                    placeholder="https://"
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, buttonLinkRu: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Текст кнопки (EN)" htmlFor="slideshow-button-text-en">
                                <Input
                                    id="slideshow-button-text-en"
                                    value={formState.buttonTextEn}
                                    placeholder="Buy tickets"
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, buttonTextEn: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Ссылка кнопки (EN)" htmlFor="slideshow-button-link-en">
                                <Input
                                    id="slideshow-button-link-en"
                                    value={formState.buttonLinkEn}
                                    placeholder="https://"
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, buttonLinkEn: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Текст кнопки (DE)" htmlFor="slideshow-button-text-de">
                                <Input
                                    id="slideshow-button-text-de"
                                    value={formState.buttonTextDe}
                                    placeholder="Tickets kaufen"
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, buttonTextDe: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Текст кнопки (UA)" htmlFor="slideshow-button-text-ua">
                                <Input
                                    id="slideshow-button-text-ua"
                                    value={formState.buttonTextUa}
                                    placeholder="Українською"
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, buttonTextUa: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Ссылка кнопки (DE)" htmlFor="slideshow-button-link-de">
                                <Input
                                    id="slideshow-button-link-de"
                                    value={formState.buttonLinkDe}
                                    placeholder="https://"
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, buttonLinkDe: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Ссылка кнопки (UA)" htmlFor="slideshow-button-link-ua">
                                <Input
                                    id="slideshow-button-link-ua"
                                    value={formState.buttonLinkUa}
                                    placeholder="Українською"
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, buttonLinkUa: event.target.value }))
                                    }
                                />
                            </Field>
                        </div>

                        {/*<Field label="Ссылка" htmlFor="slideshow-link">*/}
                        {/*    <Input*/}
                        {/*        id="slideshow-link"*/}
                        {/*        value={formState.link}*/}
                        {/*        onChange={(event) =>*/}
                        {/*            setFormState((prev) => ({ ...prev, link: event.target.value }))*/}
                        {/*        }*/}
                        {/*    />*/}
                        {/*</Field>*/}

                        <div className="form-grid form-grid-two-columns">
                            <Field label="Start at" htmlFor="slideshow-start-at">
                                <Input
                                    id="slideshow-start-at"
                                    type="datetime-local"
                                    value={formState.startAt}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, startAt: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="End at" htmlFor="slideshow-end-at">
                                <Input
                                    id="slideshow-end-at"
                                    type="datetime-local"
                                    value={formState.endAt}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, endAt: event.target.value }))
                                    }
                                />
                            </Field>
                        </div>

                        <Dropzone
                            label="Изображение слайда"
                            file={selectedFile}
                            previewUrl={localPreviewUrl ?? selectedItem?.image_url ?? null}
                            onFileChange={(file) => {
                                void handleFileChange(file);
                            }}
                            hint="Перетащите баннер в эту область или нажмите для выбора файла"
                        />

                        <div className="form-upload-meta">
                            <span className="form-upload-meta-text">
                                {formState.imageId
                                    ? `Изображение загружено. image_id: ${formState.imageId}`
                                    : "Изображение ещё не загружено."}
                            </span>

                            {isUploadingImage ? (
                                <span className="form-upload-meta-badge">Загружаем изображение...</span>
                            ) : null}
                        </div>

                        <div className="tour-sites-block">
                            <span className="tour-sites-title">Показывать на сайтах</span>
                            <label className="tour-sites-option">
                                <input
                                    type="checkbox"
                                    checked={formState.showOnMainSite}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, showOnMainSite: event.target.checked }))
                                    }
                                />
                                Основной сайт (RU / EN / DE)
                            </label>
                            <label className="tour-sites-option">
                                <input
                                    type="checkbox"
                                    checked={formState.showOnUaSite}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, showOnUaSite: event.target.checked }))
                                    }
                                />
                                Второй сайт (RU / DE / UA)
                            </label>
                            {!formState.showOnMainSite && !formState.showOnUaSite ? (
                                <span className="tour-sites-hint">
                                    Не отмечен ни один сайт — слайд не появится в hero ни на одном сайте.
                                </span>
                            ) : null}
                        </div>

                        <div className="form-switch-row">
                            <Switch
                                checked={formState.isActive}
                                onChange={(checked) =>
                                    setFormState((prev) => ({ ...prev, isActive: checked }))
                                }
                                label={formState.isActive ? "Слайд активен" : "Слайд выключен"}
                            />
                        </div>

                        {formError ? <div className="form-global-error">{formError}</div> : null}
                        {successMessage ? <div className="form-global-success">{successMessage}</div> : null}

                        <div className="editor-actions">
                            <Button type="submit" disabled={isSaving || isUploadingImage}>
                                {isSaving ? "Сохраняем..." : selectedId ? "Сохранить изменения" : "Создать слайд"}
                            </Button>
                            <Button type="button" variant="secondary" onClick={resetForm} disabled={isUploadingImage}>
                                Очистить форму
                            </Button>
                        </div>
                    </form>
                </Card>
            </section>
        </div>
    );
};

export default SlideshowsPage;
